import React, { useState, useEffect, useRef } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { pad2 } from "./helperFunctions/index.js";
import "../NewOrder.css";

const CountdownTimer = ({ countTimerStatus, currentSection, onTimerExpired }) => {
  const [duration, setDuration] = useState(-1);
  const [durationText, setDurationText] = useState("");
  const intervalRef = useRef(null);
  const prevSectionRef = useRef(currentSection);

  const cleanTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setDuration(-1);
    setDurationText("");
  };

  const startTimer = () => {
    cleanTimer();
    setDuration(0);
    intervalRef.current = setInterval(() => {
      setDuration(d => d + 1);
    }, 1000);
  };

  useEffect(() => {
    // Clear timer when component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const prevSection = prevSectionRef.current;
    prevSectionRef.current = currentSection;

    // If status is 2 (idle/stopped), stop the timer immediately
    if (countTimerStatus === 2) {
      cleanTimer();
      return;
    }

    // If moving from step 4 to step 5, ensure timer has at least 30 seconds
    if (prevSection === 4 && currentSection === 5 && duration !== -1) {
      const remainingTime = 90 - duration;
      if (remainingTime < 30) {
        setDuration(60);
      }
      return;
    }

    // If leaving steps 4 or 5 to go elsewhere (not between 4 and 5), clean up
    if (currentSection !== 4 && currentSection !== 5) {
      cleanTimer();
      return;
    }

    // Start or restart timer when in section 4 or 5 with status 1
    if ((currentSection === 4 || currentSection === 5) && countTimerStatus === 1) {
      startTimer();
    }
  }, [currentSection, countTimerStatus]);

  useEffect(() => {
    if (duration === 90) {
      cleanTimer();

      // Notify parent to handle expiration (bump back to step 4 and refresh)
      // But only if we're not already in idle state (status 2)
      if (onTimerExpired && countTimerStatus !== 2) {
        onTimerExpired();
      }
    } else if (duration !== -1) {
      setDurationText(
        pad2(Math.floor((90 - duration) / 60)) + ":" + pad2((90 - duration) % 60)
      );
    }
  }, [duration, countTimerStatus]);

  // Only render if we're in section 4 or 5 and have an active timer
  if ((currentSection !== 4 && currentSection !== 5) || duration === -1) {
    return null;
  }

  return (
    <div id="indicator_duration">
      <CircularProgressbar
        value={duration}
        text={durationText}
        minValue="0"
        maxValue="92"
        strokeWidth={6}
        styles={buildStyles({
          rotation: 0.25,
          textSize: "28px",
          pathTransitionDuration: 0.5,
          textColor: "red",
        })}
      />
    </div>
  );
};

export default CountdownTimer;